import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  Grid,
  Chip,
  Radio,
  useTheme,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ViewAgendaIcon from '@mui/icons-material/ViewAgenda';
import TableRowsIcon from '@mui/icons-material/TableRows';
import PaletteIcon from '@mui/icons-material/Palette';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useAppearance } from 'src/context/AppearanceContext';

const AppearanceModal = ({ open, onClose }) => {
  const theme = useTheme();
  const {
    mode,
    setMode,
    accentKey,
    setAccentKey,
    density,
    setDensity,
    isDark,
    resetAppearance,
    accentPresets,
  } = useAppearance();

  const themeModes = [
    {
      id: 'light',
      title: 'Light Mode',
      subtitle: 'Daytime Clinic (Standard high clarity)',
      icon: <LightModeIcon sx={{ fontSize: 24, color: '#F59E0B' }} />,
      previewBg: '#F8FAFC',
      previewCard: '#FFFFFF',
      previewText: '#0F172A',
    },
    {
      id: 'dark',
      title: 'Dark Mode',
      subtitle: 'Night Desk (Low glare for evening shifts)',
      icon: <DarkModeIcon sx={{ fontSize: 24, color: '#6366F1' }} />,
      previewBg: '#0F172A',
      previewCard: '#1E293B',
      previewText: '#F8FAFC',
    },
    {
      id: 'system',
      title: 'System Match',
      subtitle: 'Automatically adapts to device OS preference',
      icon: <SettingsBrightnessIcon sx={{ fontSize: 24, color: '#10B981' }} />,
      previewBg: isDark ? '#0F172A' : '#F8FAFC',
      previewCard: isDark ? '#1E293B' : '#FFFFFF',
      previewText: isDark ? '#F8FAFC' : '#0F172A',
    },
  ];

  const densities = [
    {
      id: 'comfortable',
      title: 'Comfortable Spacing',
      subtitle: 'Spacious layout with relaxed touch and click targets',
      icon: <ViewAgendaIcon fontSize="small" />,
    },
    {
      id: 'compact',
      title: 'Compact Density',
      subtitle: 'Dense view to see more appointments & tasks at once',
      icon: <TableRowsIcon fontSize="small" />,
    },
  ];

  const currentAccent = accentPresets[accentKey] || accentPresets.indigo;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '20px',
          bgcolor: theme.palette.background.paper,
          backgroundImage: 'none',
          boxShadow: isDark
            ? '0 24px 60px rgba(0, 0, 0, 0.8)'
            : '0 24px 60px rgba(15, 23, 42, 0.15)',
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
        },
      }}
    >
      {/* Modal Header */}
      <DialogTitle
        sx={{
          p: 3,
          pb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: '12px',
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <PaletteIcon />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.text.primary, lineHeight: 1.2 }}>
              Workspace Appearance
            </Typography>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              Industry-standard visual settings for hospital & clinic staff desks
            </Typography>
          </Box>
        </Box>

        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: theme.palette.text.secondary,
            borderRadius: '10px',
            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {/* Modal Content */}
      <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3.5 }}>
        {/* 1. Theme Mode Selection */}
        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              fontSize: '11px',
              color: theme.palette.text.secondary,
              mb: 1.5,
            }}
          >
            1. Theme Mode
          </Typography>

          <Grid container spacing={2}>
            {themeModes.map((item) => {
              const isSelected = mode === item.id;
              return (
                <Grid item xs={12} sm={4} key={item.id}>
                  <Box
                    onClick={() => setMode(item.id)}
                    sx={{
                      p: 2,
                      height: '100%',
                      borderRadius: '14px',
                      cursor: 'pointer',
                      border: isSelected
                        ? `2px solid ${theme.palette.primary.main}`
                        : `1.5px solid ${theme.palette.divider}`,
                      bgcolor: isSelected
                        ? alpha(theme.palette.primary.main, isDark ? 0.15 : 0.04)
                        : theme.palette.background.subtle,
                      transition: 'all 0.2s ease',
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      gap: 1.5,
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        transform: 'translateY(-2px)',
                        boxShadow: isDark
                          ? '0 8px 20px rgba(0,0,0,0.5)'
                          : '0 8px 20px rgba(0,0,0,0.06)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {item.icon}
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 700,
                            color: isSelected ? theme.palette.primary.main : theme.palette.text.primary,
                          }}
                        >
                          {item.title}
                        </Typography>
                      </Box>
                      {isSelected ? (
                        <CheckCircleIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                      ) : (
                        <Radio checked={false} size="small" sx={{ p: 0 }} />
                      )}
                    </Box>

                    {/* Mini Visual Preview Mock */}
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: '8px',
                        bgcolor: item.previewBg,
                        border: `1px solid ${isSelected ? alpha(theme.palette.primary.main, 0.3) : 'rgba(128,128,128,0.2)'}`,
                      }}
                    >
                      <Box
                        sx={{
                          p: 0.8,
                          borderRadius: '6px',
                          bgcolor: item.previewCard,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.8,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                        }}
                      >
                        <Box sx={{ width: 14, height: 14, borderRadius: '4px', bgcolor: theme.palette.primary.main }} />
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ width: '60%', height: 4, borderRadius: '2px', bgcolor: item.previewText, opacity: 0.8 }} />
                          <Box sx={{ width: '40%', height: 3, borderRadius: '2px', bgcolor: item.previewText, opacity: 0.4, mt: 0.4 }} />
                        </Box>
                      </Box>
                    </Box>

                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '11px', lineHeight: 1.3 }}>
                      {item.subtitle}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* 2. Clinical Theme Accent Palette */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontSize: '11px',
                color: theme.palette.text.secondary,
              }}
            >
              2. Clinic Accent Color (Brand Palette)
            </Typography>
            <Chip
              label={`Active: ${currentAccent.name}`}
              size="small"
              sx={{
                bgcolor: alpha(currentAccent.main, 0.12),
                color: currentAccent.main,
                fontWeight: 700,
                fontSize: '11px',
              }}
            />
          </Box>

          <Grid container spacing={1.5}>
            {Object.values(accentPresets).map((preset) => {
              const isSelected = accentKey === preset.id;
              return (
                <Grid item xs={6} sm={4} key={preset.id}>
                  <Box
                    onClick={() => setAccentKey(preset.id)}
                    sx={{
                      p: 1.5,
                      borderRadius: '12px',
                      cursor: 'pointer',
                      border: isSelected
                        ? `2px solid ${preset.main}`
                        : `1.5px solid ${theme.palette.divider}`,
                      bgcolor: isSelected
                        ? alpha(preset.main, isDark ? 0.15 : 0.05)
                        : theme.palette.background.subtle,
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.2,
                      '&:hover': {
                        borderColor: preset.main,
                        transform: 'translateY(-1px)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '8px',
                        background: preset.gradient,
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: `0 2px 8px ${alpha(preset.main, 0.4)}`,
                      }}
                    >
                      {isSelected && (
                        <CheckCircleIcon sx={{ color: '#FFFFFF', fontSize: 16 }} />
                      )}
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: isSelected ? 800 : 600,
                          fontSize: '12.5px',
                          color: isSelected ? preset.main : theme.palette.text.primary,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {preset.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          fontSize: '10.5px',
                          color: theme.palette.text.secondary,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {preset.subtitle}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* 3. Layout Density */}
        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              fontSize: '11px',
              color: theme.palette.text.secondary,
              mb: 1.5,
            }}
          >
            3. Information Density
          </Typography>

          <Grid container spacing={2}>
            {densities.map((item) => {
              const isSelected = density === item.id;
              return (
                <Grid item xs={12} sm={6} key={item.id}>
                  <Box
                    onClick={() => setDensity(item.id)}
                    sx={{
                      p: 1.8,
                      borderRadius: '14px',
                      cursor: 'pointer',
                      border: isSelected
                        ? `2px solid ${theme.palette.primary.main}`
                        : `1.5px solid ${theme.palette.divider}`,
                      bgcolor: isSelected
                        ? alpha(theme.palette.primary.main, isDark ? 0.15 : 0.04)
                        : theme.palette.background.subtle,
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: '10px',
                          bgcolor: isSelected ? theme.palette.primary.main : alpha(theme.palette.text.secondary, 0.1),
                          color: isSelected ? '#FFFFFF' : theme.palette.text.secondary,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {item.icon}
                      </Box>
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 700,
                            color: isSelected ? theme.palette.primary.main : theme.palette.text.primary,
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '11px' }}>
                          {item.subtitle}
                        </Typography>
                      </Box>
                    </Box>

                    {isSelected ? (
                      <CheckCircleIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                    ) : (
                      <Radio checked={false} size="small" sx={{ p: 0 }} />
                    )}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* 4. Live Healthcare Workspace Preview */}
        <Box
          sx={{
            p: 2.5,
            borderRadius: '16px',
            bgcolor: theme.palette.background.subtle,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 800, color: theme.palette.text.secondary, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Live Portal Preview
            </Typography>
            <Chip
              label={`${mode.toUpperCase()} • ${currentAccent.name} • ${density.toUpperCase()}`}
              size="small"
              sx={{
                fontSize: '10.5px',
                fontWeight: 700,
                bgcolor: alpha(theme.palette.primary.main, 0.15),
                color: theme.palette.primary.main,
              }}
            />
          </Box>

          {/* Sample Appointment Card */}
          <Box
            sx={{
              p: density === 'compact' ? 1.5 : 2,
              borderRadius: '14px',
              bgcolor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: isDark
                ? '0 4px 16px rgba(0,0,0,0.4)'
                : '0 4px 16px rgba(0,0,0,0.04)',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: '10px',
                  background: currentAccent.gradient,
                  color: '#FFFFFF',
                  fontWeight: 800,
                  fontSize: '15px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 4px 12px ${alpha(currentAccent.main, 0.3)}`,
                }}
              >
                R
              </Box>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: theme.palette.text.primary }}>
                    Rahul Sharma
                  </Typography>
                  <Chip
                    label="Attending Now"
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '10.5px',
                      fontWeight: 700,
                      bgcolor: isDark ? 'rgba(34, 197, 94, 0.2)' : '#DCFCE7',
                      color: '#16A34A',
                    }}
                  />
                </Box>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AccessTimeIcon sx={{ fontSize: 13 }} /> 10:30 AM • Dental Consultation • Dr. Mehta
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                sx={{
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.primary,
                  '&:hover': { borderColor: theme.palette.primary.main },
                }}
              >
                View Chart
              </Button>
              <Button
                variant="contained"
                size="small"
                sx={{
                  bgcolor: theme.palette.primary.main,
                  color: currentAccent.contrastText,
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.25)}`,
                  '&:hover': { bgcolor: currentAccent.dark },
                }}
              >
                Start Consult
              </Button>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      {/* Modal Actions */}
      <DialogActions
        sx={{
          p: 2.5,
          px: 3,
          display: 'flex',
          justifyContent: 'space-between',
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background.subtle,
        }}
      >
        <Button
          onClick={resetAppearance}
          startIcon={<RestartAltIcon />}
          sx={{
            color: theme.palette.text.secondary,
            '&:hover': { color: theme.palette.text.primary },
          }}
        >
          Reset to Default
        </Button>

        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            px: 3,
            py: 1,
            borderRadius: '10px',
            fontWeight: 700,
            bgcolor: theme.palette.primary.main,
            color: currentAccent.contrastText,
            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
            '&:hover': { bgcolor: currentAccent.dark },
          }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AppearanceModal;
